import { type HttpClient, query, segment } from '../http';

import {
  type ListNotificationsQuery,
  type ListNotificationsResult,
  type NotificationChannel,
  type NotificationItem,
  type NotificationPreferences,
} from './notifications.types';

/**
 * What the reader has been told.
 *
 * @param http Transport supplied by the application.
 * @returns The `notifications` domain of the API client.
 */
export function createNotificationsApi(http: HttpClient) {
  return {
    /**
     * One page of notifications, newest first.
     *
     * @param params Filter chip and pagination.
     * @returns The page, its total, and the overall unread count.
     */
    list: (params: ListNotificationsQuery = {}) =>
      http.get<ListNotificationsResult>(
        `/notifications${query({
          filter: params.filter,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * How many unread, and nothing else — what the bell badge polls.
     *
     * @returns The unread count.
     */
    unreadCount: () =>
      http.get<{ unread: number }>('/notifications/unread-count'),

    /**
     * Marks one notification read.
     *
     * @param id The notification.
     * @returns The notification as it now stands.
     * @throws 403 when it is somebody else's, 404 when it is gone.
     */
    markRead: (id: string) =>
      http.patch<NotificationItem>(`/notifications/${segment(id)}/read`, {}),

    /**
     * Marks everything read.
     *
     * @returns How many rows this actually changed.
     */
    markAllRead: () =>
      http.post<{ marked: number }>('/notifications/read-all', {}),

    /**
     * Erases one notification.
     *
     * Warning: for good. A notification is a notice, not the thing it is
     * about — erasing "Léa invited you" leaves the invitation standing.
     *
     * @param id The notification.
     * @returns Nothing.
     * @throws 403 when it belongs to somebody else, 404 when it is gone.
     */
    remove: (id: string) => http.delete<void>(`/notifications/${segment(id)}`),

    /**
     * Erases everything the reader has been told.
     *
     * @returns How many rows went.
     */
    removeAll: () => http.delete<{ removed: number }>('/notifications'),

    /**
     * Which channels the reader has silenced.
     *
     * @returns The muted channels; everything absent is on.
     */
    preferences: () =>
      http.get<NotificationPreferences>('/notifications/preferences'),

    /**
     * Replaces the whole set of silenced channels.
     *
     * Warning: sent **whole**. What is absent goes back on, so one request
     * always describes the complete state — a partial update would need the
     * caller to know what it did not send.
     *
     * Warning: `PATCH`, though it replaces everything. The API whitelists its
     * CORS methods and `PUT` is not among them, so a browser cannot send one —
     * the preflight passes and the request itself fails.
     *
     * @param muted Every channel that should be off.
     * @returns The preferences as they now stand.
     */
    setPreferences: (muted: readonly NotificationChannel[]) =>
      http.patch<NotificationPreferences>('/notifications/preferences', {
        muted,
      }),
  };
}

export type NotificationsApi = ReturnType<typeof createNotificationsApi>;
